import React, { useEffect, useState } from 'react';
import { API_URL } from '../api';
import FaqAccordion, { adaptFAQ } from '../../FAQ/FaqAccordion';

// All-FAQs browser for the Support portal: one collapsible card per FAQ set that
// exists in storage, labeled by the FAQ's name (the FAQ/<NAME>.json base name).
//
// Names come from GET /listFAQ (which enumerates the FAQ/ blob folder), so this
// lists every FAQ that actually exists — independent of the company map — and
// never fires a 404 for a company that has no FAQ. Each name is then loaded via
// the existing GET /getFAQ?company=<NAME>.
const FaqTab = () => {
  const [faqs, setFaqs]       = useState([]);   // [{ name, sections }]
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [open, setOpen]       = useState({});    // name → bool

  const load = () => {
    setLoading(true); setError('');
    fetch(`${API_URL}/listFAQ`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(async (data) => {
        const names = data.faqs || [];
        const loaded = await Promise.all(names.map(async (name) => {
          try {
            const res = await fetch(`${API_URL}/getFAQ?company=${encodeURIComponent(name)}`);
            if (!res.ok) return null;
            const sections = adaptFAQ(await res.json());
            if (sections.length === 0) return null;
            return { name, sections };
          } catch {
            return null;
          }
        }));
        setFaqs(
          loaded.filter(Boolean).sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => setError('Failed to load FAQs.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (name) => setOpen(prev => ({ ...prev, [name]: !prev[name] }));

  if (loading) return <div className="sp-loading">Loading FAQs…</div>;
  if (error)   return <div className="sp-error">{error}</div>;

  const filtered = faqs.filter(f =>
    !search || f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="sp-faq">
      <div className="sp-toolbar">
        <input
          className="sp-search"
          placeholder="🔍 Search FAQs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="sp-btn" onClick={load}>Refresh</button>
        <span className="sp-muted">
          {faqs.length} {faqs.length === 1 ? 'FAQ' : 'FAQs'}
        </span>
      </div>

      {faqs.length === 0 && <div className="sp-empty">No FAQs found in storage.</div>}
      {faqs.length > 0 && filtered.length === 0 && (
        <div className="sp-empty">No FAQs match.</div>
      )}

      <div className="sp-faq-list">
        {filtered.map((f) => {
          const isOpen = !!open[f.name];
          return (
            <div key={f.name} className="sp-faq-company">
              <button
                className={`sp-faq-company-header ${isOpen ? 'open' : ''}`}
                onClick={() => toggle(f.name)}
                aria-expanded={isOpen}
              >
                <span className="sp-faq-company-name">{f.name}</span>
                <span className="sp-faq-company-meta">
                  {f.sections.length} {f.sections.length === 1 ? 'section' : 'sections'}
                  <svg className="sp-faq-chevron" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                    <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>

              {isOpen && (
                <div className="sp-faq-company-body">
                  <FaqAccordion sections={f.sections} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FaqTab;
